
import React, { useState } from 'react';
import { Task } from '@/types/task';
import { TaskColumn } from '../TaskColumn';
import { Button } from '../ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface MobileViewProps {
  dateOffset: number;
  tasksByDay: Record<string, Task[]>;
  unscheduledTasks: Task[];
  currentDayName: string;
  onNavigate: (direction: 'prev' | 'next') => void;
  isAdmin: boolean;
  onComplete: (task: Task) => void;
  onDelete: (taskId: string) => void;
  onEdit: (taskId: string, data: any) => void;
  onReactivate?: (task: Task) => void;
}

export const MobileView = ({
  dateOffset,
  tasksByDay,
  unscheduledTasks,
  currentDayName,
  onNavigate,
  isAdmin,
  onComplete,
  onDelete,
  onEdit,
  onReactivate
}: MobileViewProps) => {
  const daysWithTasks = Object.keys(tasksByDay);
  const currentIndex = Math.abs(dateOffset) % daysWithTasks.length;
  const dayToShow = daysWithTasks[currentIndex];
  
  // Add state to track if we're showing only today's tasks
  const [showingToday, setShowingToday] = useState(false);
  
  // Function to toggle showing today's tasks
  const toggleTodayView = () => {
    setShowingToday(prev => !prev);
  };
  
  // Determine which day's tasks to display
  const displayDay = showingToday ? currentDayName : dayToShow;
  
  // Check if we have tasks for today
  const hasTodayTasks = tasksByDay[currentDayName] && tasksByDay[currentDayName].length > 0;

  return (
    <div className="flex flex-col gap-4 pb-20">
      <div className="flex items-center justify-between px-2">
        <Button
          variant="outline"
          size="icon"
          onClick={() => {
            setShowingToday(false);
            onNavigate('prev');
          }}
          className="h-8 w-8 rounded-full shrink-0"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <Button
          variant={showingToday ? "default" : "outline"}
          onClick={toggleTodayView}
          className="h-8 text-sm px-3 rounded-full shrink-0 flex items-center gap-1"
        >
          Today <ChevronRight className="h-4 w-4" />
        </Button>
        
        <Button
          variant="outline"
          size="icon"
          onClick={() => {
            setShowingToday(false);
            onNavigate('next');
          }}
          className="h-8 w-8 rounded-full shrink-0"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="w-full">
        <div className="space-y-1">
          {showingToday && hasTodayTasks ? (
            <React.Fragment>
              <TaskColumn
                day={currentDayName}
                tasks={tasksByDay[currentDayName]}
                isToday={true}
                isAdmin={isAdmin}
                onComplete={onComplete}
                onDelete={onDelete}
                onEdit={onEdit}
                onReactivate={onReactivate}
              />
              <TaskColumn
                key="unscheduled"
                day="To Do This Week"
                tasks={unscheduledTasks}
                isAdmin={isAdmin}
                onComplete={onComplete}
                onDelete={onDelete}
                onEdit={onEdit}
                onReactivate={onReactivate}
              />
            </React.Fragment>
          ) : !showingToday && displayDay ? (
            <React.Fragment>
              <TaskColumn
                day={displayDay}
                tasks={tasksByDay[displayDay]}
                isToday={displayDay === currentDayName}
                isAdmin={isAdmin}
                onComplete={onComplete}
                onDelete={onDelete}
                onEdit={onEdit}
                onReactivate={onReactivate}
              />
              <TaskColumn
                key="unscheduled"
                day="To Do This Week"
                tasks={unscheduledTasks}
                isAdmin={isAdmin}
                onComplete={onComplete}
                onDelete={onDelete}
                onEdit={onEdit}
                onReactivate={onReactivate}
              />
            </React.Fragment>
          ) : showingToday && !hasTodayTasks ? (
            <React.Fragment>
              <p className="text-gray-500 text-sm italic p-4">No tasks scheduled for today</p>
              <TaskColumn
                key="unscheduled"
                day="To Do This Week"
                tasks={unscheduledTasks}
                isAdmin={isAdmin}
                onComplete={onComplete}
                onDelete={onDelete}
                onEdit={onEdit}
                onReactivate={onReactivate}
              />
            </React.Fragment>
          ) : unscheduledTasks.length > 0 && (
            <TaskColumn
              key="unscheduled"
              day="To Do This Week"
              tasks={unscheduledTasks}
              isAdmin={isAdmin}
              onComplete={onComplete}
              onDelete={onDelete}
              onEdit={onEdit}
              onReactivate={onReactivate}
            />
          )}
        </div>
      </div>
    </div>
  );
};
